/**
 * XMLReorderer - Handles reordering of nodes and waypoints in URP XML
 * 
 * Responsible for:
 * - Reordering waypoints to match state
 * - Reordering nodes in the program tree
 * - Updating process types during reorder
 */
export class XMLReorderer {
  /**
   * Reorder waypoints in the XML DOM to match the current state
   * @param {Document} xmlDoc - The XML document
   * @param {Array<Object>} waypoints - Reordered waypoints array
   */
  reorderWaypoints(xmlDoc, waypoints) {
    if (!xmlDoc) return;

    // Group waypoints by their parent node
    const nodeGroups = new Map();
    waypoints.forEach((waypoint, newIndex) => {
      const parent = waypoint.parentNode;
      if (!parent) return;

      if (!nodeGroups.has(parent)) {
        nodeGroups.set(parent, []);
      }
      nodeGroups.get(parent).push({ waypoint, newIndex });
    });

    // Reorder nodes in MainProgram
    this.reorderNodesInMainProgram(xmlDoc, waypoints);

    // Within each node, reorder the waypoint Pose elements
    this.reorderWaypointsWithinNodes(nodeGroups);
  }

  /**
   * Reorder Contributed elements (nodes) in the MainProgram
   * @param {Document} xmlDoc - The XML document
   * @param {Array<Object>} waypoints - Waypoints with order information
   */
  reorderNodesInMainProgram(xmlDoc, waypoints) {
    const mainProgram = xmlDoc.querySelector('MainProgram');
    if (!mainProgram) return;

    // Try both uppercase and lowercase
    let mainChildren = mainProgram.querySelector('Children') || mainProgram.querySelector('children');
    if (!mainChildren) return;

    // Get all Contributed elements - try both cases
    let contributedElements = Array.from(mainChildren.querySelectorAll(':scope > Contributed'));
    if (contributedElements.length === 0) {
      contributedElements = Array.from(mainChildren.querySelectorAll(':scope > contributed'));
    }

    // Build a map of parent nodes to their order based on first waypoint appearance
    const nodeOrderMap = new Map();
    waypoints.forEach((waypoint, index) => {
      const parent = waypoint.parentNode;
      if (parent && !nodeOrderMap.has(parent)) {
        nodeOrderMap.set(parent, index);
      }
    });

    // Sort contributed elements by their first waypoint's position
    const sortedContributed = contributedElements.sort((a, b) => {
      const orderA = nodeOrderMap.get(a) ?? Infinity;
      const orderB = nodeOrderMap.get(b) ?? Infinity;
      return orderA - orderB;
    });

    // Reorder nodes in the DOM
    sortedContributed.forEach(node => {
      mainChildren.appendChild(node);
    });
  }

  /**
   * Reorder waypoints within their parent nodes
   * @param {Map} nodeGroups - Map of parent nodes to waypoint groups
   */
  reorderWaypointsWithinNodes(nodeGroups) {
    nodeGroups.forEach((waypointGroup, parentNode) => {
      // Sort by the new index order
      const sortedWaypoints = waypointGroup.sort((a, b) => a.newIndex - b.newIndex);

      // Get the parent element that contains the waypoints
      const firstElement = sortedWaypoints[0].waypoint.element;
      const container = firstElement.parentElement;

      // Reorder the Pose elements in the DOM
      sortedWaypoints.forEach(({ waypoint }) => {
        if (waypoint.element && waypoint.element.parentNode === container) {
          container.appendChild(waypoint.element);
        }
      });

      // Update process types in the XML
      sortedWaypoints.forEach(({ waypoint }) => {
        const contributed = waypoint.element.closest('Contributed');
        if (contributed) {
          const processTypeData = contributed.querySelector('data[key="process-type"]');
          if (processTypeData && waypoint.processType) {
            processTypeData.setAttribute('value', waypoint.processType);
          }
        }
      });
    });
  }
}
